import { CountryEnumOptions } from "@boundless/bl-api-client/partners/models/CountryEnumLabels"
import { CurrencyEnumOptions } from "@boundless/bl-api-client/partners/models/CurrencyEnumLabels"
import {
  getPartnersBankingDetailsApprovalRetrieveQueryKey,
  usePartnersBankingDetailsRetrieveSuspense,
  usePartnersBankingDetailsUpdate,
} from "@boundless/bl-api-client/partners/services"
import { PartnersBankingDetailsUpdateBody } from "@boundless/bl-api-client/partners/services.zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { useForm } from "react-hook-form"
import { toast } from "sonner"

import { ButtonNext } from "@/components/buttons/ButtonNext"
import { FormInput } from "@/components/form-elements/FormInput"
import { FormSection } from "@/components/form-elements/FormSection"
import { FormSelect } from "@/components/form-elements/FormSelect"
import { Form } from "@/components/ui/form"
import { Skeleton } from "@/components/ui/skeleton"
import type { ErrorType } from "@/lib/clients"
import { applyErrorsToForm, type ErrorResponseType } from "@/lib/forms/utils"

import type { z } from "zod"

type FormSchema = z.infer<typeof PartnersBankingDetailsUpdateBody>

export function BankingDetailsForm() {
  const queryClient = useQueryClient()
  const { data: bankingDetails, queryKey: bankingDetailsQueryKey } =
    usePartnersBankingDetailsRetrieveSuspense()

  const form = useForm<FormSchema>({
    resolver: zodResolver(PartnersBankingDetailsUpdateBody),
    defaultValues: {
      accountNumber: bankingDetails.accountNumber,
      address1: bankingDetails.address1,
      address2: bankingDetails.address2,
      bankName: bankingDetails.bankName,
      bic: bankingDetails.bic,
      city: bankingDetails.city,
      country: bankingDetails.country,
      currency: bankingDetails.currency,
      iban: bankingDetails.iban,
      legalName: bankingDetails.legalName,
      routingNumber: bankingDetails.routingNumber,
      zipCode: bankingDetails.zipCode,
    },
  })

  const { mutate: updateDetails } = usePartnersBankingDetailsUpdate({
    mutation: {
      onSuccess: (data) => {
        queryClient.setQueryData(bankingDetailsQueryKey, data)

        const approvalQueryKey =
          getPartnersBankingDetailsApprovalRetrieveQueryKey()
        queryClient.invalidateQueries({ queryKey: approvalQueryKey })

        toast.success("Changes submitted for approval.")
      },
      onError: (error) => {
        applyErrorsToForm(form.setError, error as ErrorType<ErrorResponseType>)
      },
    },
  })

  const submitForm = (details: FormSchema) => {
    updateDetails({ data: details })
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(submitForm)}>
        <div className="@container/form flex flex-col gap-6">
          <FormSection>
            <FormInput name="legalName" placeholder="Legal Business Name" />
            <FormSelect
              name="country"
              placeholder="Country"
              options={CountryEnumOptions}
            />
          </FormSection>

          <FormInput name="address1" placeholder="Address 1" />
          <FormInput name="address2" placeholder="Address 2" />

          <FormSection>
            <FormInput name="city" placeholder="City" />
            <FormInput name="zipCode" placeholder="Zip Code" />
            <FormSelect
              name="currency"
              placeholder="Currency"
              options={CurrencyEnumOptions}
            />
            <FormInput name="bankName" placeholder="Bank Name" />
            <FormInput name="accountNumber" placeholder="Account Number" />
            <FormInput name="bic" placeholder="BIC" />
            <FormInput name="iban" placeholder="IBAN" />
            <FormInput name="routingNumber" placeholder="Routing Number" />
          </FormSection>
        </div>

        <div className="mt-8 flex flex-row justify-end">
          <ButtonNext>Submit</ButtonNext>
        </div>
      </form>
    </Form>
  )
}

export function FallbackBankingDetailsForm() {
  return (
    <div className="flex flex-col gap-6">
      {Array.from({ length: 3 }).map((_, i) => (
        <>
          <div key={i} className="grid grid-cols-2 gap-6">
            <Skeleton className="h-12" />
            <Skeleton className="h-12" />
          </div>
          <Skeleton className="h-12" />
        </>
      ))}
    </div>
  )
}
